import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import Drawer from '@mui/material/Drawer'
import { FormControl, MenuItem, Select } from '@mui/material'
import { LuX } from 'react-icons/lu'
import Button from '../Button/Button'
import { showSnackBar } from '../Snackbar/Snackbar'
import { extractDateTime } from '../../utils/date'
import { normalizeError } from '../../utils/errorHelpers'
import { addDebtWorkStatus, getDebtWorkStatus } from '../../features/creditReports/creditReports'
import {
  DEBT_WORK_STATUS_COMMENT_LIMIT,
  DEBT_WORK_STATUS_OPTIONS,
  DEBT_WORK_STATUS_UNSIGNED_HINT,
  accountLastFour,
  debtWorkStatusLabel,
} from './debtWorkStatus'

const SELECT_SX = {
  fontSize: '12px',
  '.MuiSelect-select': {
    padding: '4px 8px',
  },
  'fieldset': {
    borderColor: 'var(--color-divider)',
  },
  '&:hover fieldset': {
    borderColor: 'var(--color-primary-main) !important',
  },
  '&.Mui-focused fieldset': {
    borderColor: 'var(--color-primary-main) !important',
    borderWidth: '1px !important',
  },
}

const MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 250,
      bgcolor: 'var(--color-bg-paper)',
      border: '1px solid var(--color-divider)',
    },
  },
}

const debtUserId = (debt) => (debt.user && typeof debt.user === 'object' ? debt.user._id : debt.user)

const knownStatus = (value) => (debtWorkStatusLabel(value) ? value : '')

const authorName = (changedBy) =>
  [changedBy?.firstName, changedBy?.lastName].filter(Boolean).join(' ') || 'Unknown user'

const heldReasonFor = (overLimit, alreadyCurrent, current) => {
  if (overLimit) return `Comment is longer than ${DEBT_WORK_STATUS_COMMENT_LIMIT} characters.`
  if (alreadyCurrent) return `Already ${debtWorkStatusLabel(current)}: add a comment to record an update.`
  return ''
}

const TimelineEntry = ({ entry }) => (
  <li className="relative ml-4 pb-5 last:pb-0">
    <span className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-primary-main" />
    <div className="font-bold">{debtWorkStatusLabel(entry.status) || entry.status}</div>
    {entry.comment ? (
      <div className="mt-1 text-sm whitespace-pre-wrap break-words">{entry.comment}</div>
    ) : null}
    <div className="mt-1 text-xs text-text-secondary">
      <span>{`by ${authorName(entry.changedBy)}`}</span>
      {' · '}
      <span>{extractDateTime(entry.createdAt)}</span>
    </div>
  </li>
)

const DebtWorkStatusPanel = ({ debt, canEdit, onClose, onSaved }) => {
  const dispatch = useDispatch()
  const debtId = debt._id
  const userId = debtUserId(debt)
  const [current, setCurrent] = useState(debt.workStatus || null)
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [status, setStatus] = useState(knownStatus(debt.workStatus))
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)
  const statusTouched = useRef(false)
  const freshSinceOpen = useRef(false)
  const panelRef = useRef(null)

  useEffect(() => {
    panelRef.current?.focus()
  }, [])

  useEffect(() => {
    let cancelled = false
    const isStale = () => cancelled || freshSinceOpen.current
    const load = async () => {
      try {
        const result = await dispatch(getDebtWorkStatus({ userId, debtId })).unwrap()
        if (isStale()) return
        setCurrent(result?.workStatus || null)
        setEntries(Array.isArray(result?.entries) ? result.entries : [])
        if (!statusTouched.current) setStatus(knownStatus(result?.workStatus))
      } catch (error) {
        if (isStale()) return
        const message = normalizeError(error)
        setLoadError(message)
        showSnackBar('error', message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [dispatch, userId, debtId])

  const trimmedComment = comment.trim()
  const overLimit = trimmedComment.length > DEBT_WORK_STATUS_COMMENT_LIMIT
  const alreadyCurrent = Boolean(status) && status === current && trimmedComment === ''
  const saveDisabled = saving || !status || overLimit || alreadyCurrent
  const heldReason = heldReasonFor(overLimit, alreadyCurrent, current)

  const handleStatusChange = (event) => {
    statusTouched.current = true
    setStatus(event.target.value)
  }

  const rereadAfterFailedSave = async () => {
    let fresh
    try {
      fresh = await dispatch(getDebtWorkStatus({ userId, debtId })).unwrap()
    } catch (error) {
      setLoadError(normalizeError(error))
      return
    }
    const serverStatus = fresh?.workStatus || null
    freshSinceOpen.current = true
    setCurrent(serverStatus)
    setEntries(Array.isArray(fresh?.entries) ? fresh.entries : [])
    setLoadError('')
    setLoading(false)
    onSaved(debtId, serverStatus)
  }

  const handleSave = async () => {
    if (saveDisabled) return
    setSaving(true)
    try {
      const result = await dispatch(addDebtWorkStatus({
        userId,
        debtId,
        status,
        comment: trimmedComment || undefined,
      })).unwrap()
      const saved = result?.workStatus || null
      freshSinceOpen.current = true
      setCurrent(saved)
      setEntries(Array.isArray(result?.entries) ? result.entries : [])
      setStatus(knownStatus(saved))
      setComment('')
      setLoadError('')
      setLoading(false)
      onSaved(debtId, saved)
    } catch (error) {
      showSnackBar('error', normalizeError(error))
      await rereadAfterFailedSave()
    } finally {
      setSaving(false)
    }
  }

  const creditor = debt.creditorName || 'Unknown creditor'
  const lastFour = accountLastFour(debt.accountNumber)
  const title = lastFour ? `${creditor} ****${lastFour}` : creditor

  const renderTimeline = () => {
    if (loading) return <div className="text-sm text-text-secondary">Loading...</div>
    if (loadError) return <div className="text-sm text-error-main">{loadError}</div>
    if (entries.length === 0) return <div className="text-sm text-text-secondary">No progress recorded yet.</div>
    return (
      <ol className="relative ml-1.5 border-l border-divider">
        {entries.map((entry, index) => (
          <TimelineEntry key={entry._id ?? index} entry={entry} />
        ))}
      </ol>
    )
  }

  const renderForm = () => {
    if (!canEdit) return null
    if (debt.isSigned !== true) {
      return (
        <div className="px-6 py-4 border-t border-divider text-sm text-text-secondary">
          {DEBT_WORK_STATUS_UNSIGNED_HINT}
        </div>
      )
    }
    return (
      <div className="flex flex-col gap-3 px-6 py-4 border-t border-divider">
        <FormControl size="small" fullWidth>
          <Select
            value={status}
            onChange={handleStatusChange}
            disabled={saving}
            displayEmpty
            inputProps={{ 'aria-label': 'Progress' }}
            sx={SELECT_SX}
            MenuProps={MENU_PROPS}
          >
            <MenuItem value="" disabled>
              <em>Select progress</em>
            </MenuItem>
            {DEBT_WORK_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className="flex flex-col gap-1">
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Comment (optional)"
            aria-label="Comment"
            rows={3}
            disabled={saving}
            className="w-full p-2 text-sm rounded-lg border border-divider bg-paper text-text-primary placeholder:text-text-secondary focus:outline-none focus:border-primary-main disabled:opacity-60"
          />
          <div className={`text-xs text-right ${overLimit ? 'text-error-main' : 'text-text-secondary'}`}>
            {`${trimmedComment.length}/${DEBT_WORK_STATUS_COMMENT_LIMIT}`}
          </div>
        </div>
        {heldReason ? <div className="text-xs text-text-secondary">{heldReason}</div> : null}
        <div className="flex justify-end">
          <Button size="sm" type="contained" disabled={saveDisabled} onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className="w-[480px] max-w-[90vw] h-full flex flex-col outline-none"
      role="dialog"
      aria-modal="true"
      aria-label="Debt progress"
    >
      <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-divider">
        <div className="min-w-0">
          <div className="font-semibold text-lg break-words">{title}</div>
          <div className="text-sm text-text-secondary">{debtWorkStatusLabel(current) || 'No progress yet'}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-1 rounded-md hover:bg-surface transition-colors text-text-secondary"
        >
          <LuX size={20} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {renderTimeline()}
      </div>
      {renderForm()}
    </div>
  )
}

const DebtWorkStatusDrawer = ({ open, debt, canEdit, onClose, onSaved }) => {
  const [lastDebt, setLastDebt] = useState(debt)

  useEffect(() => {
    if (debt) setLastDebt(debt)
  }, [debt])

  const shown = debt || lastDebt

  return (
    <Drawer anchor="right" open={Boolean(open && debt)} onClose={onClose}>
      {shown ? (
        <DebtWorkStatusPanel
          key={shown._id}
          debt={shown}
          canEdit={canEdit}
          onClose={onClose}
          onSaved={onSaved}
        />
      ) : null}
    </Drawer>
  )
}

export default DebtWorkStatusDrawer
